import { useEffect, useState } from 'react';
import type { Dpia, Incident, Risk } from '@hospasense/contracts';
import { INCIDENT_TRANSITIONS } from '@hospasense/contracts';
import {
  createRisk,
  decideDpia,
  fetchCorrectiveActions,
  fetchDpias,
  fetchIncidents,
  fetchProcessingActivities,
  fetchRisks,
  fetchVendors,
  transitionIncident,
  updateRiskStatus,
} from '../lib/queries-s5.js';
import type { CorrectiveAction, ProcessingActivity, Vendor } from '@hospasense/contracts';
import { useAuth } from '../lib/auth.js';
import {
  Badge,
  Button,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  StatusPill,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
  useToast,
} from '../components/ui/index.js';

const TABS = ['Risks', 'Incidents', 'Actions', 'Vendors', 'Processing', 'DPIAs'] as const;
type Tab = (typeof TABS)[number];

export function RiskPage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canManage = hasPermission('risk.manage');

  const [tab, setTab] = useState<Tab>('Risks');
  const [risks, setRisks] = useState<Risk[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [actions, setActions] = useState<CorrectiveAction[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [activities, setActivities] = useState<ProcessingActivity[]>([]);
  const [dpias, setDpias] = useState<Dpia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [acceptTarget, setAcceptTarget] = useState<Risk | null>(null);
  const [decideTarget, setDecideTarget] = useState<Dpia | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    likelihood: 'possible' as Risk['likelihood'],
    impact: 'moderate' as Risk['impact'],
    acceptanceReason: '',
    decision: 'approved' as 'approved' | 'rejected',
    assessment: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [r, i, a, v, p, d] = await Promise.all([
        fetchRisks(),
        fetchIncidents(),
        fetchCorrectiveActions(),
        fetchVendors(),
        fetchProcessingActivities(),
        fetchDpias(),
      ]);
      setRisks(r);
      setIncidents(i);
      setActions(a);
      setVendors(v);
      setActivities(p);
      setDpias(d);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createRisk({
        title: form.title,
        description: form.description,
        likelihood: form.likelihood,
        impact: form.impact,
      });
      toast.success('Risk registered');
      setCreateOpen(false);
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const submitAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptTarget) return;
    setSaving(true);
    try {
      await updateRiskStatus(acceptTarget.id, {
        status: 'accepted',
        acceptanceReason: form.acceptanceReason,
        expectedVersion: acceptTarget.version,
      });
      toast.success('Risk accepted with recorded reason');
      setAcceptTarget(null);
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const submitDecide = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!decideTarget) return;
    setSaving(true);
    try {
      await decideDpia(decideTarget.id, {
        decision: form.decision,
        assessment: form.assessment,
        expectedVersion: decideTarget.version,
      });
      toast.success(`DPIA ${form.decision}`);
      setDecideTarget(null);
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const doTransition = async (incident: Incident, toStatus: string) => {
    try {
      await transitionIncident(incident.id, toStatus, incident.version);
      toast.success(`Incident → ${toStatus}`);
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const ratingTone = (rating: number) =>
    rating >= 6 ? 'error' : rating >= 3 ? 'warning' : 'success';

  return (
    <div className="space-y-6" data-testid="risk-page">
      <PageHeader
        title="Risk & Privacy Registers"
        subtitle="Risk register, incidents and corrective actions, vendor/subprocessor register, data-processing inventory (RoPA) and DPIAs. Accepting a risk always records who and why."
        actions={
          canManage &&
          tab === 'Risks' && (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              Register risk
            </Button>
          )
        }
      />
      <div role="tablist" aria-label="Registers" className="flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            className={`rounded-lg px-3 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none ${
              tab === t
                ? 'bg-accent text-on-accent font-medium'
                : 'text-text-secondary hover:bg-bg-subtle'
            }`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      {error && <ErrorState description={error} onRetry={() => void load()} />}
      {loading ? (
        <LoadingState label="Loading registers…" />
      ) : (
        <TableContainer>
          {tab === 'Risks' && (
            <Table density="compact">
              <THead>
                <TR>
                  <TH>Risk</TH>
                  <TH>Likelihood × impact</TH>
                  <TH>Rating</TH>
                  <TH>Status</TH>
                  <TH>Owner</TH>
                  <TH align="right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {risks.map((r) => (
                  <TR key={r.id}>
                    <TD>
                      <p className="font-semibold text-text-primary">{r.title}</p>
                      <p className="text-xs text-text-muted">{r.description}</p>
                      {r.acceptanceReason && (
                        <p className="text-xs text-text-secondary">
                          Accepted because: {r.acceptanceReason}
                        </p>
                      )}
                    </TD>
                    <TD className="text-xs">
                      {r.likelihood} × {r.impact}
                    </TD>
                    <TD>
                      <Badge tone={ratingTone(r.rating)}>{r.rating}/9</Badge>
                    </TD>
                    <TD>
                      <StatusPill
                        tone={
                          r.status === 'accepted' || r.status === 'closed'
                            ? 'neutral'
                            : r.status === 'mitigating'
                              ? 'warning'
                              : 'error'
                        }
                        label={r.status}
                      />
                    </TD>
                    <TD className="text-xs">{r.ownerDisplayName ?? '—'}</TD>
                    <TD align="right">
                      {canManage && r.status !== 'accepted' && r.status !== 'closed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setForm((f) => ({ ...f, acceptanceReason: '' }));
                            setAcceptTarget(r);
                          }}
                        >
                          Accept…
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
          {tab === 'Incidents' && (
            <Table density="compact">
              <THead>
                <TR>
                  <TH>Incident</TH>
                  <TH>Severity</TH>
                  <TH>Status</TH>
                  <TH>Opened</TH>
                  <TH align="right">Transition</TH>
                </TR>
              </THead>
              <TBody>
                {incidents.map((i) => (
                  <TR key={i.id}>
                    <TD>
                      <p className="font-semibold text-text-primary">{i.title}</p>
                      <p className="text-xs text-text-muted">{i.summary}</p>
                    </TD>
                    <TD>
                      <Badge tone={i.severity === 'high' ? 'error' : 'warning'}>{i.severity}</Badge>
                    </TD>
                    <TD>
                      <StatusPill
                        tone={i.status === 'closed' ? 'neutral' : 'warning'}
                        label={i.status}
                      />
                    </TD>
                    <TD className="text-xs">{new Date(i.openedAt).toLocaleDateString()}</TD>
                    <TD align="right">
                      {canManage &&
                        (INCIDENT_TRANSITIONS[i.status] ?? []).map((next) => (
                          <Button
                            key={next}
                            variant="outline"
                            size="sm"
                            className="ml-1"
                            onClick={() => void doTransition(i, next)}
                          >
                            → {next}
                          </Button>
                        ))}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
          {tab === 'Actions' && (
            <Table density="compact">
              <THead>
                <TR>
                  <TH>Corrective action</TH>
                  <TH>Status</TH>
                  <TH>Due</TH>
                  <TH>Owner</TH>
                </TR>
              </THead>
              <TBody>
                {actions.map((a) => (
                  <TR key={a.id}>
                    <TD className="font-semibold text-text-primary">{a.title}</TD>
                    <TD>
                      <StatusPill
                        tone={a.status === 'done' ? 'success' : 'warning'}
                        label={a.status}
                      />
                    </TD>
                    <TD className="text-xs">
                      {a.dueAt ? new Date(a.dueAt).toLocaleDateString() : '—'}
                    </TD>
                    <TD className="text-xs">{a.ownerDisplayName ?? '—'}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
          {tab === 'Vendors' && (
            <Table density="compact">
              <THead>
                <TR>
                  <TH>Vendor</TH>
                  <TH>Purpose</TH>
                  <TH>Subprocessor</TH>
                  <TH>Data categories</TH>
                  <TH>Residency</TH>
                  <TH>DPA</TH>
                  <TH>Review due</TH>
                </TR>
              </THead>
              <TBody>
                {vendors.map((v) => (
                  <TR key={v.id}>
                    <TD className="font-semibold text-text-primary">{v.name}</TD>
                    <TD className="text-xs">{v.purpose}</TD>
                    <TD className="text-xs">{v.isSubprocessor ? 'yes' : 'no'}</TD>
                    <TD className="text-xs">{v.dataCategories.join(', ')}</TD>
                    <TD className="text-xs">{v.residency}</TD>
                    <TD>
                      <Badge tone={v.dpaSigned ? 'success' : 'error'}>
                        {v.dpaSigned ? (v.dpaReference ?? 'signed') : 'missing'}
                      </Badge>
                    </TD>
                    <TD className="text-xs">
                      {v.reviewDueAt ? new Date(v.reviewDueAt).toLocaleDateString() : '—'}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
          {tab === 'Processing' && (
            <Table density="compact">
              <THead>
                <TR>
                  <TH>Activity</TH>
                  <TH>Data categories</TH>
                  <TH>Classification</TH>
                  <TH>Legal basis</TH>
                  <TH>Retention</TH>
                  <TH>System of record</TH>
                </TR>
              </THead>
              <TBody>
                {activities.map((p) => (
                  <TR key={p.id}>
                    <TD className="font-semibold text-text-primary">{p.activity}</TD>
                    <TD className="text-xs">{p.dataCategories.join(', ')}</TD>
                    <TD>
                      <Badge tone={p.classification === 'RESTRICTED' ? 'error' : 'neutral'}>
                        {p.classification}
                      </Badge>
                    </TD>
                    <TD className="text-xs">{p.legalBasis}</TD>
                    <TD className="text-xs">{p.retention}</TD>
                    <TD className="font-mono text-xs">{p.systemOfRecord}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
          {tab === 'DPIAs' && (
            <Table density="compact">
              <THead>
                <TR>
                  <TH>DPIA</TH>
                  <TH>Status</TH>
                  <TH>Decided by</TH>
                  <TH align="right">Decision</TH>
                </TR>
              </THead>
              <TBody>
                {dpias.map((d) => (
                  <TR key={d.id}>
                    <TD>
                      <p className="font-semibold text-text-primary">{d.title}</p>
                      <p className="text-xs text-text-muted">{d.scope}</p>
                      {d.assessment && (
                        <p className="mt-1 text-xs text-text-secondary">{d.assessment}</p>
                      )}
                    </TD>
                    <TD>
                      <StatusPill
                        tone={
                          d.status === 'approved'
                            ? 'success'
                            : d.status === 'rejected'
                              ? 'error'
                              : 'warning'
                        }
                        label={d.status}
                      />
                    </TD>
                    <TD className="text-xs">{d.decidedByDisplayName ?? '—'}</TD>
                    <TD align="right">
                      {canManage && d.status === 'in_review' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setForm((f) => ({ ...f, assessment: '', decision: 'approved' }));
                            setDecideTarget(d);
                          }}
                        >
                          Decide…
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </TableContainer>
      )}

      <Modal open={createOpen} title="Register a risk" onClose={() => setCreateOpen(false)}>
        <form onSubmit={(e) => void submitCreate(e)} className="space-y-3">
          <Input
            label="Title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            minLength={5}
            required
          />
          <Textarea
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            minLength={10}
            required
            rows={3}
          />
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Likelihood"
              value={form.likelihood}
              onChange={(e) =>
                setForm((f) => ({ ...f, likelihood: e.target.value as Risk['likelihood'] }))
              }
            >
              <option value="rare">Rare</option>
              <option value="possible">Possible</option>
              <option value="likely">Likely</option>
            </Select>
            <Select
              label="Impact"
              value={form.impact}
              onChange={(e) => setForm((f) => ({ ...f, impact: e.target.value as Risk['impact'] }))}
            >
              <option value="minor">Minor</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={saving}>
              Register
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={acceptTarget !== null}
        title={`Accept risk — ${acceptTarget?.title ?? ''}`}
        onClose={() => setAcceptTarget(null)}
      >
        <form onSubmit={(e) => void submitAccept(e)} className="space-y-3">
          <p className="text-xs text-text-muted">
            Acceptance is a recorded decision: the reason below is stored on the risk and in the
            audit trail.
          </p>
          <Textarea
            label="Acceptance reason (min 10 characters)"
            value={form.acceptanceReason}
            onChange={(e) => setForm((f) => ({ ...f, acceptanceReason: e.target.value }))}
            minLength={10}
            required
            rows={3}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setAcceptTarget(null)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={saving}>
              Accept risk
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={decideTarget !== null}
        title={`Decide DPIA — ${decideTarget?.title ?? ''}`}
        onClose={() => setDecideTarget(null)}
      >
        <form onSubmit={(e) => void submitDecide(e)} className="space-y-3">
          <Select
            label="Decision"
            value={form.decision}
            onChange={(e) =>
              setForm((f) => ({ ...f, decision: e.target.value as 'approved' | 'rejected' }))
            }
          >
            <option value="approved">Approve</option>
            <option value="rejected">Reject</option>
          </Select>
          <Textarea
            label="Assessment (min 10 characters)"
            value={form.assessment}
            onChange={(e) => setForm((f) => ({ ...f, assessment: e.target.value }))}
            minLength={10}
            required
            rows={3}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setDecideTarget(null)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={saving}>
              Record decision
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
